/**
 * Tenant name resolution for list rows that carry only a tenant id.
 *
 * The collateral pages (Property Valuations, Title Searches, Lien
 * Verifications) printed the raw tenant UUID. The tenant list endpoint pages at
 * 20 by default, so resolving against its first page would miss tenants; this
 * reads each distinct id through GET /tenants/{tenantId} instead, under the
 * same query key useTenant uses, so a tenant read once is read once.
 *
 * Imported directly by those pages; not re-exported from hooks/index.ts.
 */

import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { tenantsApi } from '../api/client';

/** Where a tenant id stands: still being read, resolved to a name, or not a tenant. */
export type TenantNameState =
  | { state: 'loading' }
  | { state: 'resolved'; name: string }
  | { state: 'unresolved' };

export function useTenantNames(
  tenantIds: ReadonlyArray<string | null | undefined>,
): ReadonlyMap<string, TenantNameState> {
  const ids = Array.from(
    new Set(tenantIds.filter((id): id is string => typeof id === 'string' && id.length > 0)),
  ).sort();

  const results = useQueries({
    queries: ids.map((tenantId) => ({
      queryKey: ['tenants', tenantId],
      queryFn: () => tenantsApi.get(tenantId),
      staleTime: 5 * 60 * 1000,
      retry: false,
    })),
  });

  const entries: Array<[string, TenantNameState]> = ids.map((tenantId, index) => {
    const result = results[index];
    if (!result || result.isPending) return [tenantId, { state: 'loading' }];
    if (result.data?.name) return [tenantId, { state: 'resolved', name: result.data.name }];
    return [tenantId, { state: 'unresolved' }];
  });

  // A stable map while nothing changed, so column definitions that close over
  // it are not rebuilt on every render.
  const signature = JSON.stringify(entries);
  return useMemo(
    () => new Map(JSON.parse(signature) as Array<[string, TenantNameState]>),
    [signature],
  );
}
